/**
 * SVG sanitiser. Uploaded SVGs are rendered through <img> (which already
 * blocks scripts), but we still strip active content so the same markup is
 * safe to store, preview elsewhere or hand to prepress tools.
 */

const FORBIDDEN = new Set(['script', 'foreignobject', 'iframe', 'embed', 'object', 'audio', 'video', 'canvas', 'handler', 'listener']);

export interface SanitizedSvg {
  markup: string;
  width: number;
  height: number;
  viewBox: string | null;
  removed: number;
  hasLiveText: boolean;
  hasRaster: boolean;
}

export function sanitizeSvg(source: string): SanitizedSvg | null {
  const doc = new DOMParser().parseFromString(source, 'image/svg+xml');
  if (doc.getElementsByTagName('parsererror').length) return null;
  const svg = doc.documentElement;
  if (!svg || svg.nodeName.toLowerCase() !== 'svg') return null;

  let removed = 0;
  let hasLiveText = false;
  let hasRaster = false;

  const walk = (el: Element) => {
    for (const child of [...el.children]) {
      const name = child.localName.toLowerCase();
      if (FORBIDDEN.has(name)) {
        child.remove();
        removed++;
        continue;
      }
      if (name === 'text' || name === 'tspan' || name === 'textpath') hasLiveText = true;
      if (name === 'image') hasRaster = true;
      for (const attr of [...child.attributes]) {
        const n = attr.name.toLowerCase();
        const v = attr.value.trim().toLowerCase();
        if (n.startsWith('on')) {
          child.removeAttribute(attr.name);
          removed++;
        } else if ((n === 'href' || n === 'xlink:href') && !(v.startsWith('#') || v.startsWith('data:image/'))) {
          child.removeAttribute(attr.name);
          removed++;
        } else if (n === 'style' && /url\(\s*['"]?(?!#|data:)/.test(v)) {
          child.removeAttribute(attr.name);
          removed++;
        }
      }
      walk(child);
    }
  };
  for (const attr of [...svg.attributes]) {
    if (attr.name.toLowerCase().startsWith('on')) {
      svg.removeAttribute(attr.name);
      removed++;
    }
  }
  walk(svg);

  // Intrinsic size: width/height attributes (px/mm/cm/in/pt) or the viewBox.
  const viewBox = svg.getAttribute('viewBox');
  const vb = viewBox?.split(/[\s,]+/).map(Number);
  let width = toPx(svg.getAttribute('width'));
  let height = toPx(svg.getAttribute('height'));
  if ((!width || !height) && vb && vb.length === 4 && vb[2] > 0 && vb[3] > 0) {
    if (width && !height) height = (width * vb[3]) / vb[2];
    else if (height && !width) width = (height * vb[2]) / vb[3];
    else {
      width = vb[2];
      height = vb[3];
    }
  }
  if (!width || !height) {
    width = 1000;
    height = 1000;
  }
  // Browsers need explicit size on the root to rasterise predictably.
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));
  if (!viewBox) svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  if (!svg.getAttribute('xmlns')) svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  return {
    markup: new XMLSerializer().serializeToString(svg),
    width,
    height,
    viewBox,
    removed,
    hasLiveText,
    hasRaster,
  };
}

function toPx(v: string | null): number | null {
  if (!v) return null;
  const m = /^\s*([\d.]+)\s*(px|mm|cm|in|pt|pc)?\s*$/i.exec(v);
  if (!m) return null;
  const n = parseFloat(m[1]);
  const unit = (m[2] ?? 'px').toLowerCase();
  const k = { px: 1, mm: 96 / 25.4, cm: 96 / 2.54, in: 96, pt: 96 / 72, pc: 16 }[unit] ?? 1;
  return n > 0 ? n * k : null;
}
